import { useState, useEffect } from 'react';
import {
  Card,
  Layout,
  Text,
  Button,
  Modal,
  FormLayout,
  TextField,
  Box,
  Toast,
  Frame,
  IndexTable,
} from '@shopify/polaris';

interface Category {
  id: string;
  name: string;
  color: string;
  icon: string;
  order: number;
}

function Categories() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    color: '#000000',
    icon: '',
    order: '0',
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const API_URL = 'https://ts-team-shopify.anonnaabir.workers.dev';

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/categories`);
      const result = await response.json();
      if (result.success) {
        const mappedCategories = result.data.map((item: any) => ({
          id: item.id,
          name: item.name,
          color: item.color,
          icon: item.icon,
          order: item.order_num || 0,
        }));
        setCategories(mappedCategories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          color: formData.color,
          icon: formData.icon,
          order: parseInt(formData.order),
        }),
      });
      const result = await response.json();
      if (result.success) {
        setToastMessage('Category created successfully');
        setToastActive(true);
        setShowModal(false);
        fetchCategories();
        resetForm();
      } else {
        setToastMessage(result.error || 'Failed to create category');
        setToastActive(true);
      }
    } catch (error) {
      setToastMessage('Error creating category');
      setToastActive(true);
    }
  };

  const handleUpdate = async () => {
    if (!editingCategory) return;
    try {
      const response = await fetch(`${API_URL}/api/categories/${editingCategory.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          color: formData.color,
          icon: formData.icon,
          order: parseInt(formData.order),
        }),
      });
      const result = await response.json();
      if (result.success) {
        setToastMessage('Category updated successfully');
        setToastActive(true);
        setShowModal(false);
        fetchCategories();
        resetForm();
      } else {
        setToastMessage(result.error || 'Failed to update category');
        setToastActive(true);
      }
    } catch (error) {
      setToastMessage('Error updating category');
      setToastActive(true);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this category?')) return;
    try {
      const response = await fetch(`${API_URL}/api/categories/${id}`, {
        method: 'DELETE',
      });
      const result = await response.json();
      if (result.success) {
        setToastMessage('Category deleted successfully');
        setToastActive(true);
        fetchCategories();
      } else {
        setToastMessage(result.error || 'Failed to delete category');
        setToastActive(true);
      }
    } catch (error) {
      setToastMessage('Error deleting category');
      setToastActive(true);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      color: '#000000',
      icon: '',
      order: '0',
    });
    setEditingCategory(null);
  };

  const openEditModal = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name || '',
      color: category.color || '#000000',
      icon: category.icon || '',
      order: String(category.order || 0),
    });
    setShowModal(true);
  };

  const toastMarkup = toastActive ? (
    <Toast
      content={toastMessage}
      onDismiss={() => setToastActive(false)}
    />
  ) : null;

  const resourceName = {
    singular: 'category',
    plural: 'categories',
  };

  const rowMarkup = categories.map((category, index) => (
    <IndexTable.Row
      id={category.id}
      key={category.id}
      position={index}
    >
      <IndexTable.Cell>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div
            style={{
              width: '20px',
              height: '20px',
              borderRadius: '4px',
              backgroundColor: category.color,
            }}
          />
          <Text variant="bodyMd" as="span">
            {category.name}
          </Text>
        </div>
      </IndexTable.Cell>
      <IndexTable.Cell>{category.order}</IndexTable.Cell>
      <IndexTable.Cell>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button size="slim" onClick={() => openEditModal(category)}>
            Edit
          </Button>
          <Button size="slim" variant="primary" onClick={() => handleDelete(category.id)}>
            Delete
          </Button>
        </div>
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  return (
    <Frame>
      <Layout>
        <Layout.Section>
          <Card>
            <Box padding="400">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text variant="headingMd" as="h2">
                  Member Categories
                </Text>
                <Button
                  variant="primary"
                  onClick={() => {
                    resetForm();
                    setShowModal(true);
                  }}
                >
                  Add Category
                </Button>
              </div>
            </Box>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <IndexTable
              resourceName={resourceName}
              itemCount={categories.length}
              headings={[
                { title: 'Name' },
                { title: 'Order' },
                { title: 'Actions' },
              ]}
              selectable={false}
              loading={loading}
            >
              {rowMarkup}
            </IndexTable>
          </Card>
        </Layout.Section>
      </Layout>

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editingCategory ? 'Edit Category' : 'Add Category'}
        primaryAction={{
          content: editingCategory ? 'Update' : 'Create',
          onAction: editingCategory ? handleUpdate : handleCreate,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setShowModal(false);
              resetForm();
            },
          },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Category Name"
              value={formData.name}
              onChange={(value) => setFormData({ ...formData, name: value })}
              autoComplete="off"
            />
            <TextField
              label="Color"
              value={formData.color}
              onChange={(value) => setFormData({ ...formData, color: value })}
              autoComplete="off"
            />
            <TextField
              label="Icon (emoji or text)"
              value={formData.icon}
              onChange={(value) => setFormData({ ...formData, icon: value })}
              autoComplete="off"
            />
            <TextField
              label="Order"
              value={formData.order}
              onChange={(value) => setFormData({ ...formData, order: value })}
              type="number"
              autoComplete="off"
            />
          </FormLayout>
        </Modal.Section>
      </Modal>

      {toastMarkup}
    </Frame>
  );
}

export default Categories;
